import React from "react";
import { Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import SamplePointDetails, { formatTo3 } from "./SamplePointDetails";
import { modeLabel } from "../helper/repeatabilityText";
import { explorationRoute } from "../helper/explorationRoutes";

/**
 * Provenance and data of one palette sample, in the same presentation as the
 * exploration analyse page's "Sample point details": header facts, design
 * parameter chips, colour and width measurement cards — plus, when the sample
 * was measured on the repeatability plate, the per-mode readings.
 */
export default function SampleOriginCard({ origin, loading, error, showPlate = true, emptyText }) {
    const navigate = useNavigate();

    // Opens the exploration's analyse page on this sample: the exploration
    // is in the URL, the iteration / collection role / sample in the
    // navigation state.
    const seeInExploration = () => {
        if (!origin?.exploration?.id) return;
        navigate(explorationRoute(origin.exploration.id, "analyse"), {
            state: {
                initialTab: "designPerformance",
                iteration: origin.iteration?.number,
                collectionRole: origin.iteration?.role,
                sampleId: origin.sample_id,
            },
        });
    };

    if (loading) {
        return (
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CircularProgress size={18} thickness={5} />
                <Typography variant="body2" color="text.secondary">Loading the sample…</Typography>
            </Paper>
        );
    }
    if (error) {
        return (
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
                <Typography variant="body2" color="error">{error}</Typography>
            </Paper>
        );
    }
    if (!origin) {
        return (
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, borderStyle: "dashed" }}>
                <Typography variant="body2" color="text.secondary">
                    {emptyText || "Click a point in the plot or a patch on the grid to see where the sample came from and its design and performance space."}
                </Typography>
            </Paper>
        );
    }

    const perf = origin.performance_space || {};
    const group = origin.patch_group || {};
    const measured = origin.measured;
    const facts = [
        { label: "Sample ID", value: origin.sample_id },
        {
            label: "Comes from",
            value: origin.iteration
                ? `Iteration ${origin.iteration.number} (${origin.generation})${origin.exploration?.name ? ` · ${origin.exploration.name}` : ""}`
                : origin.exploration?.name || "Unknown",
        },
        { label: "Rank in this collection", value: group.rank ?? "-" },
        // the sample marked beneath this one (its exploration's prepass)
        ...(origin.prepass_sample_id ? [{ label: "Prepass", value: origin.prepass_sample_id }] : []),
        ...(group.x_position != null
            ? [{ label: "Position in marking", value: `Row ${group.y_position}, Column ${group.x_position}${group.identifier ? ` (${group.identifier})` : ""}` }]
            : []),
    ];

    return (
        <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }} data-testid="sample-origin-card">
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                        Sample point details
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={seeInExploration}
                        disabled={!origin.exploration?.id}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                        See in Exploration
                    </Button>
                </Stack>

                <SamplePointDetails
                    facts={facts}
                    designSpace={origin.design_space}
                    lab={perf.lab}
                    xyz={perf.xyz}
                    cameraRgb={perf.camera_rgb}
                    widthMm={perf.width_mm}
                    widthPx={perf.width_px}
                    colorTitle="Color measurements (exploration)"
                />

                {showPlate && measured && (
                    <>
                        <Divider />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>On the repeatability plate</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={`Batch ${measured.batch + 1} · row ${measured.row + 1} · column ${measured.col + 1}`} />
                            <Chip
                                size="small"
                                color={measured.repeatable && !measured.manual_rejected ? "success" : "error"}
                                label={!measured.repeatable ? "Excluded by ΔE00" : measured.manual_rejected ? "Excluded manually" : "Retained"}
                            />
                        </Stack>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {Object.entries(measured.modes).map(([mode, reading]) => (
                                <Box key={mode} sx={{ width: 168, p: 0.75, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                                    <Box sx={{ width: "100%", height: 34, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: `rgb(${reading.rgb.join(",")})` }} />
                                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 700 }}>{modeLabel(mode)}</Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>{`L* ${formatTo3(reading.lab[0])}`}</Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>{`a* ${formatTo3(reading.lab[1])}`}</Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>{`b* ${formatTo3(reading.lab[2])}`}</Typography>
                                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                        {`ΔE00 repetitions ${formatTo3(reading.deltaE)} / ${measured.limits?.[Number(mode) - 1] ?? "-"}`}
                                    </Typography>
                                    {reading.drift_deltaE != null && (
                                        <Typography variant="caption" sx={{ display: "block" }}>{`Drift ΔE00 ${formatTo3(reading.drift_deltaE)}`}</Typography>
                                    )}
                                    <Chip
                                        size="small" variant="outlined" sx={{ mt: 0.5 }}
                                        color={reading.repeatable ? "success" : "error"}
                                        label={reading.repeatable ? "Pass ΔE" : "Rejected ΔE"}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}
            </Stack>
        </Paper>
    );
}
